import { useEffect, useRef, useState } from "react";
import { DateRangePicker } from "react-date-range";
import { enUS } from "react-date-range/dist/locale";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import Modal from "react-modal";
import * as XLSX from "xlsx";
import { FiX, FiUpload } from "react-icons/fi";
import { LuListFilter } from "react-icons/lu";
import { UsersFilters } from "./UsersFilters";
import { ReceiptsFilters } from "./ReceiptsFilters";
import { WinnersFilters } from "./WinnersFilters";
import { BusinessWinnersFilters } from "./BusinessWinnersFilters";
import { labelStyle } from "./utils";

Modal.setAppElement(document.getElementById("root"));

const customStyles = {
    content: {
        position: "static",
        borderRadius: "20px",
        padding: 0,
        border: "none",
        boxShadow: "0 20px 60px rgba(0,0,0,0.18)",
        maxWidth: "92vw",
    },
    overlay: {
        background: "rgba(15, 23, 42, 0.55)",
        backdropFilter: "blur(2px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 9999,
    },
};

const CATEGORY_OPTIONS = [
    { value: "users", label: "Përdoruesit" },
    { value: "receipts", label: "Kuponat" },
    { value: "winners", label: "Fituesit" },
    { value: "business_winners", label: "Fituesit (biznese)" },
];

const DATE_LABELS = {
    users: "Data e regjistrimit",
    receipts: "Data e lëshimit të kuponit",
    winners: "Data e shpalljes së fituesit",
    business_winners: "Data e shpalljes së fituesit",
};

const FilterPopUp = ({ open, onClose, handleFormSubmit, category, params }) => {
    const [dropdownSelectItems, setDropdownSelectItems] = useState({ game_id: "", tier_level: "" });
    const [dropdownMultiSelectItems, setDropdownMultiSelectItems] = useState({ municipalities_no: [] });
    const [textItems, setTextItems] = useState({ q: "", business_nui: "" });
    // Receipts only: "" (all) | "SEF" | "PEF" | "non_fiscal". Kept apart so `type` is never sent with another category
    const [receiptType, setReceiptType] = useState("");
    const [rangeItems, setRangeItems] = useState({
        start_num_receipts: "",
        end_num_receipts: "",
        start_sum_with_vat: "",
        end_sum_with_vat: "",
        start_sum_without_vat: "",
        end_sum_without_vat: "",
        min_prize_amount: "",
        max_prize_amount: "",
    });
    const [multiSelectItems, setMultiSelectItems] = useState({
        personal_numbers: "",
        bank_account_numbers: "",
        bank_ids: "",
        municipalities_no: "",
        NUIs: "",
        coupon_ids: "",
        fiscal_device_serial_numbers: "",
    });
    const [multiSelectFilters, setMultiSelectFilters] = useState({
        personal_numbers: [],
        bank_account_numbers: [],
        bank_ids: [],
        NUIs: [],
        coupon_ids: [],
        fiscal_device_serial_numbers: [],
    });
    const [selectedCategory, setSelectedCategory] = useState("users");
    const [isCalendarOpen, setIsCalendarOpen] = useState(false);
    const [dateRange, setDateRange] = useState([
        { startDate: null, endDate: null, key: "selection" },
    ]);

    const calendarRef = useRef();

    useEffect(() => {
        if (category) setSelectedCategory(category);
    }, [category]);

    useEffect(() => {
        if (!open) return;

        const rangeParams = {};
        const multiSelectParams = {};
        const dropdownParams = {};
        const dropdownSingleParams = {};
        const textParams = {};

        const rangeKeys = [
            "start_num_receipts", "end_num_receipts",
            "start_sum_with_vat", "end_sum_with_vat",
            "start_sum_without_vat", "end_sum_without_vat",
            "min_prize_amount", "max_prize_amount",
        ];
        rangeKeys.forEach((k) => {
            if (params[k]) rangeParams[k] = params[k];
        });

        const multiKeys = [
            "personal_numbers", "bank_account_numbers", "bank_ids",
            "NUIs", "coupon_ids", "fiscal_device_serial_numbers",
        ];
        multiKeys.forEach((k) => {
            if (params[k]) multiSelectParams[k] = params[k].split(",");
        });

        if (params.municipalities_no) {
            dropdownParams.municipalities_no = params.municipalities_no.split(",").map((m) => parseInt(m));
        }
        if (params.game_id) dropdownSingleParams.game_id = parseInt(params.game_id);
        if (params.tier_level) dropdownSingleParams.tier_level = parseInt(params.tier_level);

        setReceiptType(["SEF", "PEF", "non_fiscal"].includes(params.type) ? params.type : "");
        if (params.q) textParams.q = params.q;
        if (params.business_nui) textParams.business_nui = params.business_nui;

        if (params.start_date || params.end_date) {
            setDateRange([
                {
                    startDate: params.start_date ? new Date(params.start_date) : null,
                    endDate: params.end_date ? new Date(params.end_date) : null,
                    key: "selection",
                },
            ]);
        }

        setRangeItems((prev) => ({ ...prev, ...rangeParams }));
        setMultiSelectFilters({
            personal_numbers: multiSelectParams.personal_numbers || [],
            bank_account_numbers: multiSelectParams.bank_account_numbers || [],
            bank_ids: multiSelectParams.bank_ids || [],
            NUIs: multiSelectParams.NUIs || [],
            coupon_ids: multiSelectParams.coupon_ids || [],
            fiscal_device_serial_numbers: multiSelectParams.fiscal_device_serial_numbers || [],
        });
        setDropdownMultiSelectItems((prev) => ({ ...prev, ...dropdownParams }));
        setDropdownSelectItems((prev) => ({ ...prev, ...dropdownSingleParams }));
        setTextItems((prev) => ({ ...prev, ...textParams }));
    }, [params, open]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (calendarRef.current && !calendarRef.current.contains(event.target)) {
                setIsCalendarOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const clearFilters = () => {
        setRangeItems({
            start_num_receipts: "", end_num_receipts: "",
            start_sum_with_vat: "", end_sum_with_vat: "",
            start_sum_without_vat: "", end_sum_without_vat: "",
            min_prize_amount: "", max_prize_amount: "",
        });
        setMultiSelectItems({
            personal_numbers: "", bank_account_numbers: "", bank_ids: "",
            municipalities_no: "", NUIs: "",
            coupon_ids: "", fiscal_device_serial_numbers: "",
        });
        setMultiSelectFilters({
            personal_numbers: [], bank_account_numbers: [], bank_ids: [],
            NUIs: [], coupon_ids: [], fiscal_device_serial_numbers: [],
        });
        setDropdownMultiSelectItems({ municipalities_no: [] });
        setDropdownSelectItems({ game_id: "", tier_level: "" });
        setTextItems({ q: "", business_nui: "" });
        setReceiptType("");
        setDateRange([{ startDate: null, endDate: null, key: "selection" }]);
    };

    const selectedStartDate = dateRange[0].startDate
        ? dateRange[0].startDate.toLocaleDateString("en-GB")
        : "";
    const selectedEndDate = dateRange[0].endDate
        ? dateRange[0].endDate.toLocaleDateString("en-GB")
        : "";

    const handleDropdownMultiSelectChange = (selectedOptions, actionMeta) => {
        const values = selectedOptions.map((option) => option.value);
        setDropdownMultiSelectItems({ ...dropdownMultiSelectItems, [actionMeta.name]: values });
    };

    const handleDropdownSelectChange = (selectedOption, actionMeta) => {
        const value = selectedOption?.value;
        setDropdownSelectItems({
            ...dropdownSelectItems,
            [actionMeta.name]: value === null || value === undefined ? "" : value,
        });
    };

    const handleMultiSelectFiltersChange = (e) => {
        e.preventDefault();
        const cat = e.target.elements[0].name;
        const itemState = multiSelectItems[cat] || "";
        const filterState = multiSelectFilters[cat] || [];
        if (filterState.includes(itemState)) {
            setMultiSelectFilters({ ...multiSelectFilters, [cat]: filterState.filter((i) => i !== itemState) });
            return;
        }
        if (!itemState?.length) return;
        setMultiSelectFilters({ ...multiSelectFilters, [cat]: [...filterState, itemState] });
        setMultiSelectItems({ ...multiSelectItems, [cat]: "" });
    };

    const handleDeleteMultiSelectFilters = (cat, value) => {
        setMultiSelectFilters({
            ...multiSelectFilters,
            [cat]: multiSelectFilters[cat].filter((item) => item !== value),
        });
    };

    const handleMultiSelectItemsChange = (e) => {
        setMultiSelectItems({ ...multiSelectItems, [e.target.name]: e.target.value });
    };

    const handleRangeItemsChange = (e) => {
        setRangeItems({ ...rangeItems, [e.target.name]: e.target.value });
    };

    const handleTextItemsChange = (e) => {
        setTextItems({ ...textItems, [e.target.name]: e.target.value });
    };

    const handleSubmit = () => {
        const processedMultiSelectFilters = Object.entries(multiSelectFilters).reduce((acc, [k, v]) => {
            if (Array.isArray(v) && v.length > 0) acc[k] = v.join(",");
            return acc;
        }, {});

        const processedDropdownMultiSelect = Object.entries(dropdownMultiSelectItems).reduce((acc, [k, v]) => {
            if (Array.isArray(v) && v.length > 0) acc[k] = v.join(",");
            return acc;
        }, {});

        const startISO = dateRange[0].startDate
            ? dateRange[0].startDate.toISOString().split("T")[0]
            : null;
        const endISO = dateRange[0].endDate
            ? dateRange[0].endDate.toISOString().split("T")[0]
            : null;

        const data = {
            ...processedMultiSelectFilters,
            ...rangeItems,
            ...processedDropdownMultiSelect,
            ...dropdownSelectItems,
            ...textItems,
            ...(selectedCategory === "receipts" ? { type: receiptType } : {}),
            start_date: startISO,
            end_date: endISO,
        };
        const filteredData = Object.fromEntries(
            Object.entries(data).filter(([_, v]) => !(v === "" || v == null))
        );
        handleFormSubmit(selectedCategory, filteredData);
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = (ev) => {
            const binaryStr = ev.target.result;
            const workbook = XLSX.read(binaryStr, { type: "binary" });
            const sheetName = workbook.SheetNames[0];
            const sheetData = XLSX.utils.sheet_to_json(workbook.Sheets[sheetName], { header: 1 });
            const headerIndex = sheetData.findIndex(
                (row) =>
                    Array.isArray(row) &&
                    (row.includes("Numri personal") ||
                        row.includes("Numri i bankes") ||
                        row.includes("NUI") ||
                        row.includes("Numri i kuponit") ||
                        row.includes("Numri fiskal") ||
                        row.includes("Numri serik"))
            );
            if (headerIndex === -1) return;
            const personalCol = sheetData[headerIndex].indexOf("Numri personal");
            const bankCol = sheetData[headerIndex].indexOf("Numri i bankes");
            const NUICol = sheetData[headerIndex].indexOf("NUI");
            // "Numri fiskal" is the column name older exports used for the coupon number
            const fiscalCol = sheetData[headerIndex].includes("Numri i kuponit")
                ? sheetData[headerIndex].indexOf("Numri i kuponit")
                : sheetData[headerIndex].indexOf("Numri fiskal");
            const serialCol = sheetData[headerIndex].indexOf("Numri serik");
            const dataRows = sheetData.slice(headerIndex + 1);
            const personalNumbers = dataRows.map((r) => r[personalCol]).filter(Boolean);
            const bankNumbers = dataRows.map((r) => r[bankCol]).filter(Boolean);
            const NUINumbers = dataRows.map((r) => r[NUICol]).filter(Boolean);
            const fiscalNumbers = dataRows.map((r) => r[fiscalCol]).filter(Boolean);
            const serialNumbers = dataRows.map((r) => r[serialCol]).filter(Boolean);
            setMultiSelectFilters({
                ...multiSelectFilters,
                personal_numbers: [...(multiSelectFilters.personal_numbers || []), ...personalNumbers.map(String)],
                bank_account_numbers: [...(multiSelectFilters.bank_account_numbers || []), ...bankNumbers.map(String)],
                NUIs: [...(multiSelectFilters.NUIs || []), ...NUINumbers.map(String)],
                coupon_ids: [...(multiSelectFilters.coupon_ids || []), ...fiscalNumbers.map(String)],
                fiscal_device_serial_numbers: [...(multiSelectFilters.fiscal_device_serial_numbers || []), ...serialNumbers.map(String)],
            });
        };
        reader.readAsBinaryString(file);
    };

    return (
        <Modal isOpen={open} style={customStyles} onRequestClose={onClose} contentLabel="Filterat">
            <div className="bg-white w-[560px] max-w-[92vw] max-h-[88vh] flex flex-col overflow-hidden">
                {/* Header */}
                <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100">
                    <div className="flex items-center gap-2.5">
                        <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-secondary text-white">
                            <LuListFilter size={16} />
                        </span>
                        <div>
                            <div className="text-sm font-semibold text-gray-900">Filterat</div>
                            <div className="text-[11px] text-gray-500">Përshtatni rezultatet sipas kriterëve</div>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md p-1 transition"
                        aria-label="Mbyll"
                    >
                        <FiX size={16} />
                    </button>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4 scrollbar-thin scrollbar-thumb scrollbar-track">
                    {/* Category */}
                    <div className="flex flex-col gap-1.5">
                        <label className={labelStyle}>Kategoria</label>
                        <div className="flex flex-wrap gap-1.5">
                            {CATEGORY_OPTIONS.map((opt) => {
                                const active = selectedCategory === opt.value;
                                return (
                                    <button
                                        key={opt.value}
                                        type="button"
                                        onClick={() => setSelectedCategory(opt.value)}
                                        className={`px-3 h-8 rounded-md text-xs font-semibold transition border ${
                                            active
                                                ? "bg-secondary text-white border-secondary shadow-sm"
                                                : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50"
                                        }`}
                                    >
                                        {opt.label}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* Bulk import (only relevant for users/receipts/winners) */}
                    {selectedCategory !== "business_winners" && (
                        <div>
                            <input
                                name="file"
                                id="file"
                                type="file"
                                accept=".xls, .xlsx"
                                onChange={handleFileChange}
                                className="hidden"
                            />
                            <label htmlFor="file" className="block">
                                <div className="rounded-lg border-2 border-dashed border-gray-200 hover:border-secondary/60 hover:bg-secondary/5 transition cursor-pointer px-3 py-2.5 flex items-center gap-3">
                                    <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-secondary/10 text-secondary shrink-0">
                                        <FiUpload size={14} />
                                    </span>
                                    <div className="min-w-0">
                                        <div className="text-xs text-gray-700">
                                            <span className="text-secondary font-semibold">Kliko për të ngarkuar</span> Excel
                                        </div>
                                        <div className="text-[10px] text-gray-400 truncate">
                                            Numri personal, Numri i bankes, NUI, Numri i kuponit, Numri serik
                                        </div>
                                    </div>
                                </div>
                            </label>
                        </div>
                    )}

                    {/* Category-specific filters */}
                    <div className="rounded-xl border border-gray-200/80 bg-white p-3">
                        {selectedCategory === "users" && (
                            <UsersFilters
                                multiSelectFilters={multiSelectFilters}
                                handleMultiSelectFiltersChange={handleMultiSelectFiltersChange}
                                multiSelectItems={multiSelectItems}
                                handleMultiSelectItemsChange={handleMultiSelectItemsChange}
                                rangeItems={rangeItems}
                                handleRangeItemsChange={handleRangeItemsChange}
                                dropdownMultiSelectItems={dropdownMultiSelectItems}
                                handleDropdownMultiSelectChange={handleDropdownMultiSelectChange}
                                handleDeleteMultiSelectFilters={handleDeleteMultiSelectFilters}
                            />
                        )}
                        {selectedCategory === "receipts" && (
                            <ReceiptsFilters
                                multiSelectFilters={multiSelectFilters}
                                handleMultiSelectFiltersChange={handleMultiSelectFiltersChange}
                                multiSelectItems={multiSelectItems}
                                handleMultiSelectItemsChange={handleMultiSelectItemsChange}
                                rangeItems={rangeItems}
                                handleRangeItemsChange={handleRangeItemsChange}
                                dropdownMultiSelectItems={dropdownMultiSelectItems}
                                handleDropdownMultiSelectChange={handleDropdownMultiSelectChange}
                                handleDeleteMultiSelectFilters={handleDeleteMultiSelectFilters}
                                receiptType={receiptType}
                                handleReceiptTypeChange={setReceiptType}
                            />
                        )}
                        {selectedCategory === "winners" && (
                            <WinnersFilters
                                multiSelectFilters={multiSelectFilters}
                                handleMultiSelectFiltersChange={handleMultiSelectFiltersChange}
                                multiSelectItems={multiSelectItems}
                                handleMultiSelectItemsChange={handleMultiSelectItemsChange}
                                rangeItems={rangeItems}
                                handleRangeItemsChange={handleRangeItemsChange}
                                dropdownMultiSelectItems={dropdownMultiSelectItems}
                                handleDropdownMultiSelectChange={handleDropdownMultiSelectChange}
                                handleDeleteMultiSelectFilters={handleDeleteMultiSelectFilters}
                                dropdownSelectItems={dropdownSelectItems}
                                handleDropdownSelectChange={handleDropdownSelectChange}
                            />
                        )}
                        {selectedCategory === "business_winners" && (
                            <BusinessWinnersFilters
                                textItems={textItems}
                                handleTextItemsChange={handleTextItemsChange}
                                rangeItems={rangeItems}
                                handleRangeItemsChange={handleRangeItemsChange}
                            />
                        )}
                    </div>

                    {/* Date range */}
                    <div className="flex flex-col gap-1.5 relative">
                        <label className={labelStyle}>{DATE_LABELS[selectedCategory] || "Data"}</label>
                        <div className="grid grid-cols-2 gap-2">
                            <input
                                name="start_date"
                                value={selectedStartDate}
                                readOnly
                                onClick={() => setIsCalendarOpen(true)}
                                placeholder="Nga"
                                className="h-9 px-3 rounded-lg border border-gray-200 bg-white text-sm placeholder-gray-400 cursor-pointer focus:outline-none focus:ring-2 focus:ring-secondary/30 focus:border-secondary transition"
                            />
                            <input
                                name="end_date"
                                value={selectedEndDate}
                                readOnly
                                onClick={() => setIsCalendarOpen(true)}
                                placeholder="Deri"
                                className="h-9 px-3 rounded-lg border border-gray-200 bg-white text-sm placeholder-gray-400 cursor-pointer focus:outline-none focus:ring-2 focus:ring-secondary/30 focus:border-secondary transition"
                            />
                        </div>
                        {isCalendarOpen && (
                            <div className="report-date-range absolute z-40 bottom-14 right-0 rounded-xl shadow-lg border border-gray-200 bg-white" ref={calendarRef}>
                                <DateRangePicker
                                    ranges={dateRange}
                                    direction="horizontal"
                                    locale={enUS}
                                    onChange={(item) => setDateRange([item.selection])}
                                    moveRangeOnFirstSelection={false}
                                    showSelectionPreview={false}
                                    staticRanges={[]}
                                    inputRanges={[]}
                                    rangeColors={["#D2AB67"]}
                                />
                            </div>
                        )}
                    </div>
                </div>

                {/* Footer */}
                <div className="flex justify-between items-center gap-2 px-5 py-3 border-t border-gray-100 bg-gray-50/60">
                    <button
                        type="button"
                        onClick={clearFilters}
                        className="inline-flex items-center gap-1.5 h-9 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 px-3 text-xs font-semibold text-gray-700 transition"
                    >
                        Reset
                    </button>
                    <div className="inline-flex items-center gap-1.5">
                        <button
                            type="button"
                            onClick={onClose}
                            className="inline-flex items-center gap-1.5 h-9 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 px-3 text-xs font-semibold text-gray-700 transition"
                        >
                            Anulo
                        </button>
                        <button
                            type="button"
                            onClick={handleSubmit}
                            className="inline-flex items-center gap-1.5 h-9 rounded-lg bg-secondary hover:bg-secondary/90 active:scale-[0.98] px-4 text-xs font-semibold text-white shadow-sm transition"
                        >
                            Vazhdo
                        </button>
                    </div>
                </div>
            </div>
        </Modal>
    );
};

export default FilterPopUp;
